'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Zap, Map as MapIcon, ChevronRight, ChevronDown, ChevronLeft, Book, Target } from 'lucide-react';

export interface LessonNode {
  id: string;
  name: string;
  title: string;
  path: string;
  type: 'dir' | 'file';
  roleGate?: string;
  badge?: string;
  children: LessonNode[];
  userHasRole?: boolean;
}

export type NodeStatus = 'locked' | 'active' | 'luminescent';

export interface GamificationState {
  [slug: string]: {
    isGamified: boolean;
    status: NodeStatus;
    progressState?: 'not-started' | 'in-progress' | 'completed';
  };
}

interface GamifiedTreeProps {
  lessonTree: LessonNode[];
  gamificationState: GamificationState;
  onNodeClick: (node: LessonNode) => void;
  focusedNode?: LessonNode | null;
  onFocusSelect?: (node: LessonNode) => void;
  activeParentId?: string | null;
  onActiveParentChange?: (parentId: string) => void;
  showParentTabs?: boolean;
}

// Helper to extract the slug from the filename (e.g., 'game-dynamics-unit-1.md' -> 'game-dynamics-unit-1')
const getSlug = (id: string) => id.replace('.md', '');

/**
 * The GamifiedTree is the visualization of the curriculum.
 * It enforces the Artist's principles:
 * - Next-Action Primacy Card (S1 Orientation)
 * - Tactile Affordances (Framer Motion scale on hover/tap)
 * - Visual Trust States (Luminescent, Active, Locked)
 */
export default function GamifiedTree({
  lessonTree,
  gamificationState,
  onNodeClick,
  focusedNode,
  onFocusSelect,
  activeParentId: controlledActiveParentId,
  onActiveParentChange,
  showParentTabs = true,
}: GamifiedTreeProps) {
  const sortedParents = [...lessonTree].sort((a, b) => a.title.localeCompare(b.title));
  const [internalActiveParentId, setInternalActiveParentId] = React.useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('gamified-tree-active-tab');
        if (saved) return JSON.parse(saved);
      } catch (e) {}
    }
    return null;
  });
  const activeParentId = controlledActiveParentId ?? internalActiveParentId;
  const setActiveParentId = (nextParentId: string) => {
    if (controlledActiveParentId === undefined) {
      setInternalActiveParentId(nextParentId);
    }
    onActiveParentChange?.(nextParentId);
  };

  // Initialize on first render
  React.useEffect(() => {
    if (sortedParents.length === 0) {
      return;
    }

    const hasCurrentParent = activeParentId
      ? sortedParents.some((parent) => parent.id === activeParentId)
      : false;

    if (!hasCurrentParent) {
      setActiveParentId(sortedParents[0].id);
    }
  }, [sortedParents, activeParentId]);

  React.useEffect(() => {
    if (controlledActiveParentId === undefined && activeParentId) {
      localStorage.setItem('gamified-tree-active-tab', JSON.stringify(activeParentId));
    }
  }, [controlledActiveParentId, activeParentId]);

  const activeParent = sortedParents.find(p => p.id === activeParentId) || sortedParents[0];
  
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  React.useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    const container = scrollContainerRef.current;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        container!.scrollLeft += e.deltaY;
      }
    };
    
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    
    return () => {
      window.removeEventListener('resize', checkScroll);
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [sortedParents]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 250;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="flex flex-col relative w-full">
      {showParentTabs && (
        <div className="w-full border-b border-border/40 mb-4 pb-3 flex justify-center">
          <div className="relative w-full max-w-[500px] group">
            <div className={`absolute -left-2 top-0 bottom-0 w-12 bg-gradient-to-r from-background via-background to-transparent z-10 pointer-events-none flex items-center justify-start transition-opacity duration-300 ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`}>
              <button 
                onClick={() => scroll('left')}
                className="h-6 w-6 ml-0 flex items-center justify-center bg-background border border-border/50 shadow-sm rounded-full text-muted-foreground hover:text-primary hover:border-primary/50 opacity-0 group-hover:opacity-100 transition-all pointer-events-auto"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
            </div>

            <div 
              ref={scrollContainerRef}
              onScroll={checkScroll}
              className="flex overflow-x-auto gap-3 no-scrollbar w-full relative z-0 px-1"
            >
            {sortedParents.map(parent => {
              const isLocked = parent.userHasRole === false;
              const isActive = activeParent?.id === parent.id;
              return (
                <button
                  key={parent.id}
                  onClick={() => {
                    setActiveParentId(parent.id);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border whitespace-nowrap transition-all ${
                    isActive 
                      ? 'bg-primary/20 border-primary/40 text-primary shadow-sm' 
                      : isLocked 
                        ? 'bg-muted/30 border-border/50 text-muted-foreground/60 opacity-60' 
                        : 'bg-muted/50 border-border/80 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {isLocked && <Lock className="w-3 h-3 opacity-50" />}
                  <span className="text-[10px] font-bold uppercase tracking-widest">{parent.title}</span>
                </button>
              );
            })}
            </div>

            <div className={`absolute -right-2 top-0 bottom-0 w-12 bg-gradient-to-l from-background via-background to-transparent z-10 pointer-events-none flex items-center justify-end transition-opacity duration-300 ${canScrollRight ? 'opacity-100' : 'opacity-0'}`}>
              <button 
                onClick={() => scroll('right')}
                className="h-6 w-6 mr-0 flex items-center justify-center bg-background border border-border/50 shadow-sm rounded-full text-muted-foreground hover:text-primary hover:border-primary/50 opacity-0 group-hover:opacity-100 transition-all pointer-events-auto"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* THE HIERARCHICAL NODE TREE for the Active Parent */}
      {activeParent && (
        <div className="flex flex-col gap-6 relative pl-2 pr-6 w-full">
          {/* Draw a faint connection line behind the entire tree */}
          <div className="absolute left-6 top-4 bottom-10 w-0.5 bg-border/40 z-0" />
          
          <RecursiveTree 
            nodes={activeParent.children} 
            gamificationState={gamificationState} 
            onNodeClick={onNodeClick} 
            onFocusSelect={onFocusSelect}
            depth={1} 
            parentDir={activeParent}
          />
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Internal Components for the Recursive Tree
// ----------------------------------------------------------------------

interface TreeProps {
  nodes: LessonNode[];
  gamificationState: GamificationState;
  onNodeClick: (node: LessonNode) => void;
  onFocusSelect?: (node: LessonNode) => void;
  depth: number;
  parentDir?: LessonNode;
}

const RecursiveTree = ({ nodes, gamificationState, onNodeClick, onFocusSelect, depth, parentDir }: TreeProps) => {
  // Sort: files first (so Base Lessons appear above role directories), then alphabetical
  const sortedNodes = [...nodes].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'file' ? -1 : 1;
    return a.title.localeCompare(b.title);
  });

  return (
    <>
      {sortedNodes.map((node) => {
        if (node.type === 'dir') {
          return (
            <TreeNodeDir 
              key={node.path}
              node={node}
              nodes={nodes}
              gamificationState={gamificationState}
              onNodeClick={onNodeClick}
              onFocusSelect={onFocusSelect}
              depth={depth}
              parentDir={parentDir}
            />
          );
        }

        // It's a file. Check if it's gamified.
        const slug = getSlug(node.id);
        const state = gamificationState[slug];

        // If it's not gamified, we can optionally hide it or show it as plain text. 
        // For the gamification map, we only want to highlight actual interactive units.
        // We will render it quietly if it's not gamified, to maintain the structure.
        if (!state || !state.isGamified) {
          const isLockedByRole = node.userHasRole === false;
          return (
             <button 
               key={node.path} 
               onClick={() => {
                 if (!isLockedByRole) {
                   if (parentDir) onFocusSelect?.(parentDir);
                   onNodeClick(node);
                 }
               }}
               disabled={isLockedByRole}
               className={`flex items-center gap-4 py-2 relative z-10 pl-2 text-left w-full hover:bg-muted/30 rounded-lg transition-colors ${isLockedByRole ? 'opacity-30 cursor-not-allowed' : 'opacity-70 hover:opacity-100 cursor-pointer'}`}
             >
                <div className="w-2 h-2 rounded-full bg-border shrink-0 z-10" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-foreground truncate block">{node.title}</span>
                </div>
                {isLockedByRole && <Lock className="w-3 h-3 text-muted-foreground/50 shrink-0 mr-2" />}
             </button>
          );
        }

        return (
          <GamifiedNode 
            key={node.path} 
            node={node} 
            status={state.status} 
            onNodeClick={(clickedNode) => {
              if (parentDir) onFocusSelect?.(parentDir);
              onNodeClick(clickedNode);
            }} 
          />
        );
      })}
    </>
  );
};

interface NodeProps {
  node: LessonNode;
  status: NodeStatus;
  onNodeClick: (node: LessonNode) => void;
}

const GamifiedNode = ({ node, status, onNodeClick }: NodeProps) => {
  const isLockedByRole = node.userHasRole === false;
  const isLocked = status === 'locked' || isLockedByRole;
  const isActive = status === 'active' && !isLockedByRole;
  const isLuminescent = status === 'luminescent' && !isLockedByRole;

  // Tactile visual states
  const bgColor = isLuminescent ? 'bg-primary/20 border-primary/50' : isActive ? 'bg-primary border-primary' : 'bg-muted border-border';
  const iconColor = isLuminescent ? 'text-primary' : isActive ? 'text-primary-foreground' : 'text-muted-foreground';
  const textColor = isLuminescent ? 'text-primary' : isActive ? 'text-foreground font-bold' : 'text-muted-foreground';

  return (
    <div className="p-2 w-full">
      <motion.button
        whileHover={!isLocked ? { scale: 1.02, x: 5 } : undefined}
        whileTap={!isLocked ? { scale: 0.98 } : undefined}
        onClick={() => !isLocked && onNodeClick(node)}
        disabled={isLocked}
        tabIndex={0}
        className={`relative z-10 flex items-center gap-4 p-3 rounded-xl w-full text-left transition-all ${isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        {/* The Node Enclosure (Dot/Badge) */}
        <div className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 shadow-sm transition-colors ${bgColor}`}>
          {isLocked ? (
            <Lock className={`w-4 h-4 ${iconColor}`} />
          ) : isLuminescent ? (
            <Zap className={`w-5 h-5 ${iconColor}`} />
          ) : (
            <div className="w-2 h-2 rounded-full bg-primary-foreground animate-pulse" />
          )}
        </div>

        {/* The Label */}
        <div className="flex flex-col flex-1 min-w-0">
          <h4 className={`text-sm truncate ${textColor}`}>
            {node.title}
          </h4>
          <div className="flex items-center gap-2 mt-1">
            {node.badge && (
               <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-foreground/5 text-foreground/70 rounded">
                 {node.badge}
               </span>
            )}
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
               {isLocked ? 'Locked' : isLuminescent ? 'Mastered' : 'Available'}
            </span>
          </div>
        </div>

        {/* Glowing Edge Effect if Active/Luminescent */}
        {(isActive || isLuminescent) && (
          <div className="absolute inset-0 rounded-xl border border-primary/20 pointer-events-none shadow-[inset_0_0_20px_rgba(var(--primary),0.05)]" />
        )}
      </motion.button>
    </div>
  );
};

// ----------------------------------------------------------------------
// Stateful Directory Component
// ----------------------------------------------------------------------
const TreeNodeDir = ({ node, gamificationState, onNodeClick, onFocusSelect, depth }: TreeProps & { node: LessonNode }) => {
  const [isExpanded, setIsExpanded] = React.useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(`gamified-tree-expanded-${node.id}`);
        if (saved !== null) return JSON.parse(saved);
      } catch (e) {}
    }
    
    // Depth 1 (Units) start collapsed by default
    // Depth 2+ (Roles) start expanded if the user has access, collapsed if locked
    if (depth >= 2) {
      return node.userHasRole !== false;
    }
    
    return false;
  });

  React.useEffect(() => {
    localStorage.setItem(`gamified-tree-expanded-${node.id}`, JSON.stringify(isExpanded));
  }, [isExpanded, node.id]);

  return (
    <div className="flex flex-col relative z-10 w-full mt-4">
      <div className={`flex items-center gap-3 bg-background relative mb-4 transition-opacity w-full text-left group ${node.userHasRole === false ? 'opacity-50 hover:opacity-70' : 'hover:opacity-70'}`}>
        
        {/* EXPAND / COLLAPSE TRIGGER */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 z-10 transition-colors cursor-pointer ${node.userHasRole === false ? 'bg-muted/30 border-border/50 text-muted-foreground' : 'bg-muted border-border hover:bg-primary/10 hover:border-primary/30 hover:text-primary'}`}
        >
            {node.userHasRole === false ? (
              <Lock className="w-3.5 h-3.5 opacity-50" />
            ) : isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
            )}
        </button>

        {/* FOCUS TRIGGER */}
        <button 
          onClick={() => {
            onFocusSelect?.(node);
          }}
          className={`group/focus flex-1 flex items-center justify-between min-w-0 text-left cursor-pointer transition-all px-3 py-2 rounded-md border border-transparent ${node.userHasRole === false ? 'text-muted-foreground/70' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 hover:border-border/50 hover:shadow-sm'}`}
        >
          <div className="truncate">
            {depth === 2 ? (
              <div className="flex items-baseline gap-1.5 truncate">
                <span className="text-[9px] lowercase opacity-60 font-medium tracking-wide">for</span>
                <span className="text-[10px] font-bold uppercase tracking-widest">{node.title}</span>
                <span className="text-[9px] lowercase opacity-60 font-medium tracking-wide">role</span>
              </div>
            ) : (
              <h3 className="text-xs font-bold uppercase tracking-widest truncate">
                {node.title}
              </h3>
            )}
          </div>
          
          {node.userHasRole !== false && (
            <div className="opacity-0 group-hover/focus:opacity-100 flex items-center text-[10px] uppercase tracking-wider text-muted-foreground transition-opacity">
              Focus <Target className="w-3 h-3 ml-1.5" />
            </div>
          )}
        </button>
      </div>
      {isExpanded && (
        <div className="flex flex-col gap-2 ml-4 relative animate-in fade-in slide-in-from-top-2 duration-300">
          <RecursiveTree 
            nodes={node.children} 
            gamificationState={gamificationState} 
            onNodeClick={onNodeClick} 
            onFocusSelect={onFocusSelect} 
            depth={depth + 1} 
            parentDir={node} 
          />
        </div>
      )}
    </div>
  );
};
